import * as Tone from 'tone'
import { InstrumentBase } from './InstrumentBase';
import units from "../units.json"
import { getAudioGraphInstance } from '../audioEngine';
import { LooperUI } from "../projectViews/stationViews/LooperUI";
import { addKeysAsField } from '../utils/misc';
import store, { setClipLength, setClipParameter } from '../store';
import colors from "../../styles/colors.module.scss";
import { getSampleLibraryManagerInstance, SamplePlayer } from '../SamplePlayer';
import { limitToNyquist } from '../utils/parameters';
import { getScaleForSampleName } from "$utils/music"; 
import { staticSelect } from '../utils/react';

export class Looper extends InstrumentBase {
    static metadata = {
        tipus: 'looper',
        tipusDisplay: 'Looper',
        versio: '0.1',
        color:  colors.blue,
        hasLogo: false,
        hasMiniature: false,
    }
    static parametersDescription = addKeysAsField({
        ...super.parametersDescription,
        transpose: {type: 'float', min: -12, max: 12, step: 1, initial: 0, bind: "clip"},
        start: {type: 'float', min:0, max:1, initial: 0, bind: "clip"},
        end: {type: 'float', min:0, max:1, initial: 1, bind: "clip"},
        speed: {type: 'float', min: 0.5, max: 2, step: 0.01, initial: 1, bind: "clip"},
        isReverse: {type: 'bool', initial: false, bind: "clip"},
        lpf_cutoff: {type: 'float', unit: units.hertz, min: 400, max: 12000, initial: 12000, logarithmic: true, bind: "preset"},
    }, "nom");
    static pitchRange = {
        ...super.pitchRange,
        usable: {min: 0, max: 0}
    };

    needsRetriggeringPlayer = false; // This is used to know if we need to retrigger the player at the next step because some parameter that requires retriggering has changed (like loop points, speed, mode...)

    getUserInterfaceComponent() {
        return LooperUI
    }

    get player() {
        return this.audioNodes.samplePlayer;
    }

    async buildInstrumentAudioGraph() {
        await super.buildInstrumentAudioGraph();

        const gain = new Tone.Gain(Tone.dbToGain(0));
        const lpf = new Tone.Filter(500, 'lowpass', -12).connect(gain);
        const samplePlayer = new SamplePlayer().connect(lpf);
        samplePlayer.loop = true;
        
        this.audioNodes = {
            gain: gain,
            samplePlayer: samplePlayer,
            lpf: lpf
        };
 
        this.addEffectChainNodes(gain, this.bus);
        this.isGraphBuilt = true;

        // Pre-load sound from current clip (if any)
        const {soundName} = this.getPlaybackClip().sound;
        if (soundName) this.setSound(soundName);
    }

    setParameterInAudioGraph(name, value) {
        if (!getAudioGraphInstance().isGraphBuilt()) return;
        switch (name) {
            case "transpose":
            case "start":
            case "end":
            case "isReverse":
            case "speed":
                this.player[name] = value;
                break;
            case "lpf_cutoff":
                this.audioNodes.lpf.frequency.rampTo(limitToNyquist(value), 0.01);
                break;
        }
    }

    setSound = soundName => this.player.loadSound(soundName);
    unsetSound = () => this.player.unloadSound();


    playerIsPlaying() {
        return this.player.isPlaying();
    }


    stopSound(time) {
        if (!getAudioGraphInstance().isGraphBuilt()) return false;
        this.player.stop(time);
    }

    
    adjustClipLengthToFit() {
        const start = staticSelect(this.getDisplayParameterSelector("start"));
        const end = staticSelect(this.getDisplayParameterSelector("end"));
        const clipLength = staticSelect(this.getDisplayClipLengthSelector());
        const unwarpedSliceSeconds = (end - start) * this.getRawDisplaySoundDuration()
        const speed = staticSelect(this.getDisplayParameterSelector("speed"));
        const warpedSliceSeconds = unwarpedSliceSeconds / speed;
        const newClipLength = Math.round(warpedSliceSeconds / this.getStepDuration());
        if (clipLength === newClipLength) return;
        store.dispatch(setClipLength({instrumentID: this.id, clipNumber: staticSelect(this.getDisplayParameterSelector("clipNumber")), value: newClipLength}));
        
        // // let's calculate the speed so that it corresponds to the rounded clip length
        // const clipSeconds = newClipLength * this.getStepDuration();
        // const realSpeed = clipSeconds / unwarpedSliceSeconds;
        // store.dispatch(setClipParameter({instrumentID: this.id, parameter: "speed", value: realSpeed}));

    }

    adjustSpeedToFit() {
        const start = staticSelect(this.getDisplayParameterSelector("start"));
        const end = staticSelect(this.getDisplayParameterSelector("end"));
        const clipLength = staticSelect(this.getDisplayClipLengthSelector());
        const unwarpedSliceSeconds = (end - start) * this.getRawDisplaySoundDuration();
        const clipSeconds = clipLength * this.getStepDuration();
        const speed = clipSeconds / unwarpedSliceSeconds;
        store.dispatch(setClipParameter({instrumentID: this.id, parameter: "speed", value: speed}));
    }

    // not calling this.player.getUnwarpedSoundDuration because we want this function to work
    // even without audiograph built
    getRawDisplaySoundDuration() {
        const {soundName} = staticSelect(this.getDisplayClipSelector()).sound;
        return getSampleLibraryManagerInstance().getBufferForSound(soundName).duration;

    }

    onSequencerStep(currentMainSequencerStep, time) {
        const playbackClip = this.getPlaybackClip();
        if (playbackClip == null) return this.unsetSound();

        const {soundName} = playbackClip.sound;

        // If no sound name, do nothing (except stop sound if for some reason it was playing)
        if (soundName == null) return this.unsetSound();

        // If there is a clip active, but its sound is different from the current sound in buffer, change 
        // the sound and do nothing else. When the sound is loaded, it will be triggered starting at the right point
        if (soundName !== this.player.currentSoundInBufferName) return this.setSound(soundName);
 

        // If we reach this point, it means that the looper should be playing a sound. We trigger the sound (if not playing) an estimate
        // The position at which the playback should be triggered. Also, we adjust the clip length in case some parameters have changed that
        // would have an effect on that.
        const currentStep = currentMainSequencerStep % playbackClip.length;
        let slicePosition = currentStep / playbackClip.length;

        if (isNaN(slicePosition)) slicePosition = 0; // happens when getPlaybackClip().length is 0
        this.player.onSlicePosition(time, slicePosition);

    }

    onTransportStop() {
        this.stopSound()
    }

    onStopAllSounds() {
        this.stopSound()
    }
    
    adjustPitchToInstrumentRange(pitch) {
        return 0;
    }

    onMidiNote(pitch, midiVelocity, noteOff, extras) {
        // Nothing, the looper is not triggered by MIDI notes
    }
}